import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import AgoraRTC, { IAgoraRTCRemoteUser, ICameraVideoTrack, IMicrophoneAudioTrack } from "agora-rtc-sdk-ng";
import VideoControls from "./VideoControls";
import EndInterviewModal from "./EndInterviewModal";
import TimeWarningModal from "./TimeWarningModal";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useInterviewApi } from "src/_common/hooks/actions/interview/appInterviewApiHook";
import { useScreeningApi } from "src/_common/hooks/actions/screening/appScreeningApiHook";
import { useAgoraRecordingApi } from "src/_common/hooks/actions/agora/appAgoraRecordingApiHook";
import { useAppUserDetailsSelector } from "src/_common/hooks/selectors/userSelector";
import { CANDIDATE_STATUS, INTERVIEW_STATUS } from "src/_config";
import "./interview-video.css";

interface InterviewVideoParams {
  interviewId: string;
}

interface VideoCallData {
  schedule_id: string;
  screening_id: string;
  job_title: string | null;
  interview_schedule: string;
  status: number;
  status_text: string;
  member_id: number;
  jobseeker_id: number;
  video_call_data: {
    appId: string;
    channelName: string;
    host: {
      uid: number;
      name: string;
      token: string;
      role: string;
    };
    guest: {
      uid: number;
      name: string;
      token: string;
      role: string;
    };
    expireTime: number;
    expiresIn: number;
    isExpired: boolean;
  };
  interview_url: string;
}

const InterviewVideo: React.FC = () => {
  const { interviewId } = useParams<InterviewVideoParams>();
  const history = useHistory();
  const toast = useToaster();
  const interviewApi = useInterviewApi();
  const screeningApi = useScreeningApi();
  const agoraRecordingApi = useAgoraRecordingApi();
  const userDetails = useAppUserDetailsSelector();

  const [isJoined, setIsJoined] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [joinError, setJoinError] = useState<string | null>(null);
  const [videoCallData, setVideoCallData] = useState<VideoCallData | null>(null);
  const [remoteUsers, setRemoteUsers] = useState<IAgoraRTCRemoteUser[]>([]);
  const [localVideoTrack, setLocalVideoTrack] = useState<ICameraVideoTrack | null>(null);
  const [localAudioTrack, setLocalAudioTrack] = useState<IMicrophoneAudioTrack | null>(null);
  const [isVideoMuted, setIsVideoMuted] = useState(false);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [client] = useState(() => AgoraRTC.createClient({ mode: "rtc", codec: "vp8" }));
  const [showEndInterviewModal, setShowEndInterviewModal] = useState(false);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [isRecordingStarted, setIsRecordingStarted] = useState(false);
  const [recordingResourceId, setRecordingResourceId] = useState<string | null>(null);
  const [recordingSid, setRecordingSid] = useState<string | null>(null);
  const [remainingTime, setRemainingTime] = useState<number>(30 * 60); // 30 minutes in seconds
  const [showTimeWarning, setShowTimeWarning] = useState<boolean>(false);
  const [timeWarningShown, setTimeWarningShown] = useState<boolean>(false);

  // User names from API
  const [localUserName, setLocalUserName] = useState<string>("You");
  const [remoteUserName, setRemoteUserName] = useState<string>("Participant");

  useEffect(() => {
    if (!interviewId) {
      return;
    }
    fetchVideoCallData();

    return () => {
      // Cleanup when component unmounts
      cleanupCall();
    };
  }, [interviewId]);

  // Timer effect - counts down from 30 minutes to 0
  useEffect(() => {
    if (isLoading || !isJoined || joinError) return;

    const timerInterval = setInterval(() => {
      setRemainingTime((prevTime) => {
        if (prevTime <= 0) {
          return 0;
        }
        return prevTime - 1;
      });
    }, 1000);

    return () => clearInterval(timerInterval);
  }, [isLoading, isJoined, joinError]);

  // Check for 5-minute warning (when remaining time is 5 minutes = 300 seconds)
  useEffect(() => {
    // Show warning at 5 minutes remaining if not shown yet
    if (remainingTime === 300 && !timeWarningShown) {
      setShowTimeWarning(true);
      setTimeWarningShown(true);
    }
  }, [remainingTime, timeWarningShown]);

  // Automatically end call when time reaches 0
  useEffect(() => {
    if (remainingTime === 0 && isJoined) {
      setShowTimeWarning(false); // Hide warning modal when interview ends
      toast.warning("Interview time has ended");
      leaveCall(false);
    }
  }, [remainingTime, isJoined]);

  // Play local video when track is available and component is mounted
  useEffect(() => {
    if (localVideoTrack && isJoined && isMediaLoaded) {
      const localPlayer = document.getElementById("local-player");
      if (localPlayer) {
        try {
          localVideoTrack.play("local-player");
          console.log("Local video playing");
        } catch (error) {
          console.error("Error playing local video:", error);
        }
      }
    }
  }, [localVideoTrack, isJoined, isMediaLoaded]);

  const fetchVideoCallData = () => {
    setIsLoading(true);

    interviewApi.callGetVideoCallData(
      { interviewId },
      (message: any, response: any) => {
        const videoData = response;

        setVideoCallData(videoData);
        setIsLoading(false);
        // Initialize Agora client after fetching data
        initializeAgoraClient(videoData);
      },
      (error: any) => {
        toast.error("Failed to fetch interview details. Please try again.");
        setIsLoading(false);
        //
      }
    );
  };

  const startRecording = () => {
    if (!videoCallData || isRecordingStarted) {
      return;
    }

    const recordingData = {
      channel_name: videoCallData.video_call_data.channelName,
      uid: videoCallData.video_call_data.host.uid.toString(),
      schedule_id: videoCallData.schedule_id,
    };

    agoraRecordingApi.callStartRecording(
      recordingData,
      (message: string, response: any) => {
        setIsRecordingStarted(true);
        // Store resourceId and sid from the response
        if (response?.resourceId) {
          setRecordingResourceId(response.resourceId);
        }
        if (response?.sid) {
          setRecordingSid(response.sid);
        }
        toast.success(message || "Recording started");
        console.log("Recording started successfully:", response);
      },
      (error: string) => {
        console.error("Failed to start recording:", error);
        toast.error("Failed to start recording");
      }
    );
  };

  const stopRecording = () => {
    if (!videoCallData || !isRecordingStarted || !recordingResourceId || !recordingSid) {
      console.warn("Cannot stop recording: missing required data", {
        isRecordingStarted,
        hasResourceId: !!recordingResourceId,
        hasSid: !!recordingSid,
      });
      return;
    }

    const recordingData = {
      channel_name: videoCallData.video_call_data.channelName,
      uid: videoCallData.video_call_data.host.uid.toString(),
      resource_id: recordingResourceId,
      sid: recordingSid,
      schedule_id: videoCallData.schedule_id,
    };

    agoraRecordingApi.callStopRecording(
      recordingData,
      (message: string, response: any) => {
        setIsRecordingStarted(false);
        setRecordingResourceId(null);
        setRecordingSid(null);
        toast.success(message || "Recording stopped");
        console.log("Recording stopped successfully:", response);
      },
      (error: string) => {
        console.error("Failed to stop recording:", error);
        toast.error("Failed to stop recording");
      }
    );
  };

  const handleToggleRecording = () => {
    if (isRecordingStarted) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const handleUserPublished = async (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
    try {
      await client.subscribe(user, mediaType);

      // Add user to remoteUsers if not already present (for both audio and video)
      setRemoteUsers((prev) => {
        const exists = prev.find((u) => u.uid === user.uid);
        if (exists) {
          // Update the existing user reference
          return prev.map((u) => (u.uid === user.uid ? user : u));
        }
        return [...prev, user];
      });

      if (mediaType === "video") {
        // Play remote video
        // setTimeout(() => {
        const player = document.getElementById(`remote-player-${user.uid}`);

        if (player && user.videoTrack) {
          user.videoTrack.play(`remote-player-${user.uid}`);
        }
        // }, 100);
      }

      if (mediaType === "audio" && user.audioTrack) {
        // Add a small delay to ensure the audio track is fully ready
        // setTimeout(() => {
        if (user.audioTrack) {
          // Set volume to maximum to ensure audio is audible
          user.audioTrack.setVolume(100);
          // Play the audio track
          user.audioTrack.play();
        }
        // }, 100);
      }
    } catch (error) {}
  };

  const handleUserUnpublished = (user: IAgoraRTCRemoteUser, mediaType: "audio" | "video") => {
    // Keep the user in the list, just update their reference
    // The video/audio track will be undefined and we'll show appropriate indicators
    setRemoteUsers((prev) => {
      return prev.map((u) => (u.uid === user.uid ? user : u));
    });
  };

  const handleUserJoined = (user: IAgoraRTCRemoteUser, showNotification: boolean = true) => {
    // Add user to the list when they join
    setRemoteUsers((prev) => {
      const exists = prev.find((u) => u.uid === user.uid);
      if (exists) {
        return prev;
      }
      return [...prev, user];
    });
    if (showNotification) {
      toast.show("A participant has joined the interview");
    }
  };

  const handleUserLeft = (user: IAgoraRTCRemoteUser) => {
    setRemoteUsers((prev) => {
      const filtered = prev.filter((u) => u.uid !== user.uid);

      return filtered;
    });
    toast.show("A participant has left the interview");
  };

  const initializeAgoraClient = async (callData?: VideoCallData) => {
    try {
      if (!callData) {
        const errorMsg = "Invalid video call data - No data received";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!callData.video_call_data) {
        const errorMsg = "Invalid video call data ";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      const { appId, channelName, host, guest } = callData.video_call_data;

      // Validate required fields
      if (!appId || !channelName) {
        const errorMsg = "Invalid video call configuration";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Trim whitespace and validate appId format
      const trimmedAppId = appId.trim();
      if (trimmedAppId.length === 0) {
        const errorMsg = "Invalid Room ID";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      if (!host && !guest) {
        const errorMsg = "Invalid video call configuration ";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Determine if current user is host or guest based on member_id/jobseeker_id
      let credentials;
      let userRole = "unknown";
      let isHost = false;

      // Check if user is authenticated and has user details
      if (userDetails) {
        // if (userDetails.user_type == "member" && callData.member_id == userDetails?.member?.user_id) {
        if (callData.member_id == userDetails?.member?.user_id) {
          // Current user is the host (employer/member)
          credentials = host;
          userRole = "host (member)";
          isHost = true;
        } else {
          // Current user is the guest (jobseeker)
          credentials = guest;
          userRole = "guest (jobseeker)";
          isHost = false;
        }
      }

      // If credentials not determined yet, use fallback logic
      if (!credentials) {
        // Try to use guest by default for unauthenticated users or unknown cases
        credentials = guest || host;
        userRole = "fallback (using guest or host)";
        isHost = false;
        console.warn("Could not determine user role, using fallback credentials");
        console.warn("Using:", credentials === guest ? "guest" : "host");
      }

      if (!credentials || !credentials.token) {
        const errorMsg = "Invalid authentication credentials - No token available";
        toast.error(errorMsg);
        setJoinError(errorMsg);
        setIsLoading(false);
        return;
      }

      // Set user names based on role
      if (isHost) {
        setLocalUserName(host?.name || "You");
        setRemoteUserName(guest?.name || "Participant");
      } else {
        setLocalUserName(guest?.name || "You");
        setRemoteUserName(host?.name || "Participant");
      }

      // Set up event listeners BEFORE joining

      client.on("user-published", async (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          await handleUserPublished(user, mediaType);
        }
      });

      client.on("user-unpublished", (user, mediaType) => {
        if (mediaType === "audio" || mediaType === "video") {
          handleUserUnpublished(user, mediaType);
        }
      });

      client.on("user-left", (user) => {
        handleUserLeft(user);
      });

      client.on("user-joined", (user) => {
        handleUserJoined(user);
      });

      await client.join(trimmedAppId, channelName, credentials.token, credentials.uid);

      // Wait a moment for the channel state to update
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Check if there are already users in the channel
      const existingUsers = client.remoteUsers;

      // If current user is not the host (member), check if host is already in the channel
      // if (!isHost) {
      //   const hostIsPresent = existingUsers.some((user) => user.uid === host?.uid);

      //   if (!hostIsPresent) {
      //     // Host is not present, leave and show error
      //     await client.leave();
      //     history.push("/dashboard");
      //     toast.error("The interview host has not joined yet. Please wait for the host to join first.");
      //     setIsLoading(false);
      //     return;
      //   }
      // }

      // Add existing users to state first (don't subscribe yet)
      for (const user of existingUsers) {
        handleUserJoined(user, false);
      }

      // Create local tracks
      const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();

      setLocalAudioTrack(audioTrack);
      setLocalVideoTrack(videoTrack);

      // Publish local tracks
      await client.publish([audioTrack, videoTrack]);

      // Update state to show the UI (this renders the DOM elements)
      setIsJoined(true);
      setIsMediaLoaded(true);

      // Play local video after a short delay to ensure DOM is ready
      setTimeout(() => {
        const localPlayer = document.getElementById("local-player");
        if (localPlayer) {
          videoTrack.play("local-player");
        } else {
          console.error("local-player element not found");
          // Retry after another delay
          setTimeout(() => {
            videoTrack.play("local-player");
          }, 500);
        }
      }, 100);

      // NOW subscribe to existing users after DOM is ready
      setTimeout(async () => {
        for (const user of existingUsers) {
          // Subscribe to their audio track if they have published audio
          if (user.hasAudio) {
            try {
              await client.subscribe(user, "audio");
              // After subscribing, the audioTrack should be available
              if (user.audioTrack) {
                // Set volume and play the audio track
                user.audioTrack.setVolume(100);
                user.audioTrack.play();
              }
            } catch (error) {
              console.error("Error subscribing to existing user audio:", error);
            }
          }

          // Subscribe to their video track if they have published video
          if (user.hasVideo) {
            try {
              await client.subscribe(user, "video");
              // After subscribing, play the video
              if (user.videoTrack) {
                setTimeout(() => {
                  const player = document.getElementById(`remote-player-${user.uid}`);
                  if (player) {
                    user.videoTrack!.play(`remote-player-${user.uid}`);
                    console.log(`Playing remote video for user ${user.uid}`);
                  } else {
                    console.error(`remote-player-${user.uid} element not found`);
                  }
                }, 100);
              }
            } catch (error) {
              console.error("Error subscribing to existing user video:", error);
            }
          }
        }
      }, 300);

      toast.show("You joined the interview");
    } catch (error: any) {
      let errorMessage = "Failed to join the interview";

      if (error?.code === "INVALID_VENDOR_KEY" || error?.message?.includes("invalid vendor key")) {
        errorMessage = "Invalid configuration. Please contact support.";
      } else if (error?.code === "INVALID_TOKEN" || error?.message?.includes("token")) {
        errorMessage = "Invalid or expired authentication token. Please refresh and try again.";
      } else if (error?.message) {
        errorMessage = `Failed to join the interview`;
      }

      setJoinError("Unable to join the interview at this time");
      setIsLoading(false);
      setIsMediaLoaded(true);
      // toast.error(errorMessage);
    }
  };

  const toggleVideo = async () => {
    if (localVideoTrack) {
      await localVideoTrack.setEnabled(isVideoMuted);
      setIsVideoMuted(!isVideoMuted);
    }
  };

  const toggleAudio = async () => {
    if (localAudioTrack) {
      await localAudioTrack.setEnabled(isAudioMuted);
      localAudioTrack.setVolume(100);
      setIsAudioMuted(!isAudioMuted);
      remoteUsers.forEach((user) => {
        if (user.audioTrack) {
          user.audioTrack.setVolume(100);
        }
      });
    }
  };

  const handleEndCallClick = () => {
    // Check if current user is the host (member)
    const isCurrentUserHost =
      userDetails?.user_type === "member" && videoCallData?.member_id === userDetails?.member?.user_id;

    if (isCurrentUserHost) {
      // Show confirmation modal for host
      setShowEndInterviewModal(true);
    } else {
      // Directly leave for non-host users
      leaveCall(true);
    }
  };

  const cleanupCall = () => {
    // Synchronous cleanup for component unmount (doesn't navigate)
    try {
      // Stop recording if it was started
      if (isRecordingStarted && recordingResourceId && recordingSid && videoCallData) {
        stopRecording();
      }

      // Close local tracks
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      // Remove all event listeners
      client.removeAllListeners();

      // Leave the channel
      if (isJoined) {
        client.leave().catch(() => {
          // Ignore errors during cleanup
        });
      }
    } catch (error) {
      // Ignore errors during cleanup
    }
  };

  const leaveCall = async (showToast: boolean = false) => {
    try {
      // Stop recording if it was started
      if (isRecordingStarted) {
        stopRecording();
      }

      // Close local tracks
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }

      // Remove remote users
      setRemoteUsers([]);

      // Remove all event listeners
      client.removeAllListeners();

      // Leave the channel
      if (isJoined) {
        await client.leave();
      }

      setIsJoined(false);

      if (showToast) {
        toast.show("You have left the interview");
      }

      history.push("/dashboard");
    } catch (error) {}
  };

  const handleEndInterviewConfirm = () => {
    if (!videoCallData?.screening_id) {
      toast.error("Unable to end interview: Missing screening ID");
      setShowEndInterviewModal(false);
      return;
    }

    // First, call API to update interview status to 3 (completed)
    if (videoCallData?.schedule_id) {
      interviewApi.callRescheduleOrCancel(
        {
          schedule_id: videoCallData.schedule_id,
          interview_status: INTERVIEW_STATUS.COMPLETE,
        },
        (message: string, resp: any) => {
          console.log("Interview status updated to completed:", resp);
          // Then update screening status
          screeningApi.callUpdateStatus(
            {
              screening_id: videoCallData.screening_id,
              status: CANDIDATE_STATUS.INTERVIEW_DONE,
            },
            (message: string, resp: any) => {
              setShowEndInterviewModal(false);
              leaveCall(false);
            },
            (error: string) => {
              toast.error("Failed to update interview status. Please try again.");
              setShowEndInterviewModal(false);
            }
          );
        },
        (error: string) => {
          console.error("Failed to update interview status:", error);
          toast.error("Failed to complete interview. Please try again.");
          setShowEndInterviewModal(false);
        }
      );
    } else {
      // If schedule_id is missing, just update screening status
      screeningApi.callUpdateStatus(
        {
          screening_id: videoCallData.screening_id,
          status: CANDIDATE_STATUS.INTERVIEW_DONE,
        },
        (message: string, resp: any) => {
          setShowEndInterviewModal(false);
          leaveCall(false);
        },
        (error: string) => {
          toast.error("Failed to update interview status. Please try again.");
          setShowEndInterviewModal(false);
        }
      );
    }
  };

  const handleEndInterviewCancel = () => {
    setShowEndInterviewModal(false);
  };

  // Format remaining time as MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  const handleTimeWarningClose = () => {
    setShowTimeWarning(false);
  };

  return (
    <div className="drjob-iv-main-container">
      {/* Drjob Logo */}
      <div className="drjob-iv-logo-container" style={{ textAlign: "center", marginBottom: 24 }}>
        <img src="/images/dr-logo.png" alt="Drjob Logo" style={{ height: 48, objectFit: "contain" }} />
      </div>
      {isLoading ? (
        <div className="drjob-iv-header-section">
          <h1 className="drjob-iv-header-title">Loading Interview...</h1>
          <p className="drjob-iv-header-id">Please wait</p>
        </div>
      ) : (
        // : joinError ? (
        //   <>
        //     <div className="drjob-iv-header-section">
        //       <h1 className="drjob-iv-header-title">Interview Session</h1>
        //     </div>
        //     <div className="drjob-iv-error-container">
        //       <div className="drjob-iv-error-content">
        //         <i
        //           className="fa fa-exclamation-circle"
        //           style={{ fontSize: "64px", color: "#e74c3c", marginBottom: "20px" }}
        //         ></i>
        //         <h2 style={{ color: "white", fontSize: "1.5rem", marginBottom: "16px" }}>Connection Failed</h2>
        //         <p style={{ color: "white", fontSize: "1.1rem", marginBottom: "24px", opacity: 0.9 }}>{joinError}</p>
        //         <button
        //           onClick={() => history.push("/dashboard")}
        //           style={{
        //             padding: "12px 32px",
        //             fontSize: "1rem",
        //             fontWeight: "600",
        //             color: "white",
        //             background: "#3498db",
        //             border: "none",
        //             borderRadius: "8px",
        //             cursor: "pointer",
        //             transition: "background 0.3s ease",
        //           }}
        //           onMouseOver={(e) => (e.currentTarget.style.background = "#2980b9")}
        //           onMouseOut={(e) => (e.currentTarget.style.background = "#3498db")}
        //         >
        //           Return to Dashboard
        //         </button>
        //       </div>
        //     </div>
        //   </>
        // )
        <>
          <div className="drjob-iv-header-section">
            <h1 className="drjob-iv-header-title">Interview Session</h1>
            {/* Remaining Time Display */}
            {isJoined && (
              <div className={`drjob-iv-remaining-time ${remainingTime <= 300 ? "time-warning" : "time-normal"}`}>
                <i className="fa fa-clock-o"></i>
                <span>Remaining Time: {formatTime(remainingTime)}</span>
              </div>
            )}
            {/* <p className="drjob-iv-header-id">Room ID: {videoCallData?.video_call_data?.channelName || interviewId}</p> */}
          </div>

          <div className="drjob-iv-video-grid">
            {/* Local Video */}
            <div className="drjob-iv-video-wrapper drjob-iv-local-stream">
              <div id="local-player" className="drjob-iv-video-element"></div>
              {isMediaLoaded && !localVideoTrack && !isVideoMuted && (
                <div className="drjob-iv-video-unavailable">
                  <div className="drjob-iv-unavailable-content">
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>Camera not available</p>
                    <p style={{ fontSize: "0.85rem", marginTop: "8px", opacity: 0.8 }}>
                      Please check camera permissions
                    </p>
                  </div>
                </div>
              )}
              {isVideoMuted && localVideoTrack && (
                <div className="drjob-iv-video-unavailable">
                  <div className="drjob-iv-unavailable-content">
                    <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                    <p>Video is turned off</p>
                  </div>
                </div>
              )}
              {isMediaLoaded && !localAudioTrack && !isAudioMuted && (
                <div className="drjob-iv-audio-unavailable-indicator">
                  <i className="fa fa-exclamation-circle" style={{ fontSize: "20px" }}></i>
                  <span style={{ marginLeft: "8px" }}>Microphone not available</span>
                </div>
              )}
              <div className="drjob-iv-stream-label">
                {localUserName} {isVideoMuted && <span className="drjob-iv-muted-indicator">(Video Off)</span>}
                {isAudioMuted && <span className="drjob-iv-muted-indicator">(Muted)</span>}
                {isMediaLoaded && !localVideoTrack && !isVideoMuted && (
                  <span className="drjob-iv-muted-indicator">(No Camera)</span>
                )}
                {isMediaLoaded && !localAudioTrack && !isAudioMuted && (
                  <span className="drjob-iv-muted-indicator">(No Mic)</span>
                )}
              </div>
            </div>

            {/* Remote Videos */}
            {remoteUsers.map((user) => (
              <div key={user.uid} className="drjob-iv-video-wrapper drjob-iv-remote-stream">
                <div id={`remote-player-${user.uid}`} className="drjob-iv-video-element"></div>
                {!user.videoTrack && (
                  <div className="drjob-iv-video-unavailable">
                    <div className="drjob-iv-unavailable-content">
                      <i className="fa fa-video-slash" style={{ fontSize: "48px", marginBottom: "12px" }}></i>
                      <p>Participant's video is unavailable</p>
                    </div>
                  </div>
                )}
                {!user.audioTrack && user.videoTrack && (
                  <div className="drjob-iv-audio-unavailable-indicator">
                    <i className="fa fa-microphone-slash" style={{ fontSize: "20px" }}></i>
                    <span style={{ marginLeft: "8px" }}>Audio unavailable</span>
                  </div>
                )}
                <div className="drjob-iv-stream-label">
                  {remoteUserName}
                  {!user.videoTrack && <span className="drjob-iv-muted-indicator"> (Video Off)</span>}
                  {!user.audioTrack && <span className="drjob-iv-muted-indicator"> (Muted)</span>}
                </div>
              </div>
            ))}

            {/* Placeholder when no remote users */}
            {remoteUsers.length === 0 && (
              <div className="drjob-iv-video-wrapper drjob-iv-waiting-state">
                <div className="drjob-iv-waiting-content">
                  <div className="drjob-iv-waiting-icon">
                    <i className="fa fa-users"></i>
                  </div>
                  <p>Waiting for other participants to join...</p>
                </div>
              </div>
            )}
          </div>

          {/* Video Controls */}
          {!isLoading && (
            <VideoControls
              isVideoMuted={isVideoMuted}
              isAudioMuted={isAudioMuted}
              onToggleVideo={toggleVideo}
              onToggleAudio={toggleAudio}
              onLeaveCall={handleEndCallClick}
              isRecording={isRecordingStarted}
              onToggleRecording={handleToggleRecording}
              showRecordingButton={
                userDetails?.user_type === "member" && videoCallData?.member_id === userDetails?.member?.user_id
              }
            />
          )}
        </>
      )}

      {/* End Interview Confirmation Modal */}
      {showEndInterviewModal && (
        <EndInterviewModal
          isVisible={showEndInterviewModal}
          onConfirm={handleEndInterviewConfirm}
          onCancel={handleEndInterviewCancel}
        />
      )}

      {/* Time Warning Modal */}
      {showTimeWarning && (
        <TimeWarningModal
          isVisible={showTimeWarning}
          onClose={handleTimeWarningClose}
          remainingSeconds={remainingTime}
        />
      )}
    </div>
  );
};

export default InterviewVideo;
